import { queryOptions } from "@tanstack/react-query";
import {
  addCardioAlternate as addCardioAlternateApi,
  copyWorkout,
  createWorkout as createWorkoutApi,
  getWorkout,
  listWorkouts,
  makeWorkoutItemAlternateMain,
  removeWorkout,
  removeWorkoutItemAlternate,
  reorderWorkoutItemAlternates,
  setWorkoutLabels,
  updateWorkoutItem,
} from "api-client";

import {
  type AddCardioAlternateInput,
  type CreateWorkoutInput,
  type ReorderWorkoutItemAlternatesInput,
  type UpdateCardioItemInput,
  type WorkoutDetail,
  type WorkoutList,
  type WorkoutItemAlternateInput,
  type WorkoutListInput,
  type WorkoutsRepository,
} from "./WorkoutsRepository";

export function createPersonalLibraryWorkoutsRepository() {
  const rootKey = ["workouts", "personal-library"] as const;
  const repository = {
    listOptions(input: WorkoutListInput) {
      return queryOptions<WorkoutList>({
        queryKey: [...rootKey, "list", input],
        async queryFn() {
          const response = await listWorkouts({ query: input });
          return response.data.data;
        },
      });
    },
    async createWorkout(input: CreateWorkoutInput) {
      const response = await createWorkoutApi({
        body: {
          ...input,
          isInLibrary: true,
        },
      });
      return response.data.data;
    },
    async copyWorkout(workoutId: string) {
      const response = await copyWorkout({ path: { workoutId } });
      return response.data.data;
    },
    detailOptions(workoutId: string) {
      return queryOptions<WorkoutDetail>({
        queryKey: [...rootKey, "detail", workoutId],
        async queryFn() {
          const response = await getWorkout({ path: { workoutId } });
          return response.data.data;
        },
      });
    },
    async removeWorkout(workoutId: string) {
      return removeWorkout({ path: { workoutId } });
    },
    async setWorkoutLabels(workoutId: string, labelIds: string[]) {
      return setWorkoutLabels({
        path: { workoutId },
        body: { labelIds },
      });
    },
    async addCardioAlternate({
      workoutId,
      workoutItemId,
      ...body
    }: AddCardioAlternateInput) {
      const response = await addCardioAlternateApi({
        path: { workoutId, workoutItemId },
        body,
      });
      return response.data.data;
    },
    async updateCardioItem({
      workoutId,
      workoutItemId,
      ...body
    }: UpdateCardioItemInput) {
      const response = await updateWorkoutItem({
        path: { workoutId, workoutItemId },
        body,
      });
      return response.data.data;
    },
    async removeWorkoutItemAlternate(input: WorkoutItemAlternateInput) {
      return removeWorkoutItemAlternate({ path: input });
    },
    async makeWorkoutItemAlternateMain(input: WorkoutItemAlternateInput) {
      const response = await makeWorkoutItemAlternateMain({ path: input });
      return response.data.data;
    },
    async reorderWorkoutItemAlternates({
      alternateIds,
      ...path
    }: ReorderWorkoutItemAlternatesInput) {
      return reorderWorkoutItemAlternates({ path, body: { alternateIds } });
    },
  };

  return repository satisfies WorkoutsRepository;
}
